import { useMachine } from '@xstate/react';
import React, { createContext, useState } from 'react';

import type { FeatureCategories } from 'src/modules/designStudio/constants';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import type { WindowItemType } from 'src/modules/designStudio/constants/hardCodedData/windowParametricData';
import type { TemplateWallAppearance } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  applyDesignUpdateThroughTemplateSystem,
  changeAppearanceOfWallMaterialGroup,
  getCurrentDesignConfig,
  getGuardedLegacyConfig,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { macheteBuildingRef } from 'src/modules/designStudio/machines/building/ref';
import {
  createMachine,
  initialContext,
} from 'src/modules/designStudio/machines/history';
import {
  getProductSelectedAttributes,
  isProductSelected,
  newTrackingDesignConfig,
} from 'src/modules/designStudio/machines/history/helpers';
import type { ExpandedProductSelectedAttributes } from 'src/modules/designStudio/machines/history/helpers';
import type {
  Context,
  ProductSelectedTrackingData,
} from 'src/modules/designStudio/machines/history/types';
import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import type { DesignStudioColor } from 'src/shared/graphql/generated';
import { useMode, useSplit, useTranslate } from 'src/shared/hooks';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { useToaster } from 'src/shared/providers/Toaster';
import { Route } from 'src/shared/route';
import type { ProductSelected } from 'src/shared/telemetry';
import { productSelected } from 'src/shared/telemetry';

import { Mode } from '../ModeProvider';

export const defaultContext = {
  historyState: initialContext,
  updateDesign: () => undefined,
  resetDesign: () => undefined,
  undoDesignChange: () => undefined,
  redoDesignChange: () => undefined,
  canRedo: false,
  canUndo: false,
  updateDesignConfigFromBuilding: () => {},
  createInspireMeImage: () => {},
  clearInspireMeImage: () => {},
  waitForInspireMeImage: () => {},
  setInspireMeImage: () => {},
};

export type UpdateDesignFunction = ({
  category,
  update,
  trackingData,
}: UpdateDesignProps) => void;

type UpdateDesignPropsTrackingData = Omit<
  ProductSelectedTrackingData,
  'design_studio_mode'
>;

interface UpdateDesignProps {
  selectedWindow?: WindowItemType;
  selectedTexture?: MenuItemType;
  selectedColor?: DesignStudioColor;
  category: FeatureCategories;
  update: {
    targetAppearanceGroup?: TemplateWallAppearance;
    textureId?: number | null;
    color?: string | null;
    gridStyle?: string | null;
    frameStyle?: string | null;
  };
  trackingData: UpdateDesignPropsTrackingData;
}

const context = createContext<{
  historyState: Context;
  updateDesign: (config: UpdateDesignProps) => void;
  resetDesign: () => void;
  undoDesignChange: () => void;
  redoDesignChange: () => void;
  canRedo: boolean;
  canUndo: boolean;
  updateDesignConfigFromBuilding: () => void;
  createInspireMeImage: () => void;
  clearInspireMeImage: () => void;
  waitForInspireMeImage: () => void;
  setInspireMeImage: (image: string) => void;
}>(defaultContext);

const ConnectedProvider = ({
  authToken,
  children,
  jobId,
  setIsNotConnectedToFirestore,
  shareToken,
  viewUserId,
}: {
  authToken?: string | null;
  children: React.ReactNode;
  jobId: number;
  setIsNotConnectedToFirestore: (isNotConnectedToFirestore: boolean) => void;
  shareToken?: string;
  viewUserId?: number;
}) => {
  const mode = useMode();
  const { addToast, removeToast } = useToaster();
  const translator = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { client: splitClient } = useSplit();
  const skipProfile = [Mode.GUEST, Mode.PREVIEW, Mode.SHARE].includes(mode);

  const [machineState, send] = useMachine(() =>
    createMachine({
      jobId,
      authToken,
      splitClient,
      shareToken,
      addToast,
      removeToast,
      translator,
      viewUserId,
      mode,
    }),
  );

  if (machineState.value === 'notConnectedToFirestore') {
    setIsNotConnectedToFirestore(true);
  }

  const { canRedo, canUndo } = machineState.context;
  useAuthExperience({ skipProfile, skipBranch: true });

  const resetDesign = () => {
    send({
      type: 'RESET_DESIGN',
    });
  };

  const updateDesign = ({
    selectedWindow,
    selectedTexture,
    selectedColor,
    category,
    update,
    trackingData,
  }: UpdateDesignProps) => {
    const expandedTrackingData = {
      ...trackingData,
      design_studio_mode: mode,
    };

    send({
      type: 'UPDATE_DESIGN_LEGACY',
      category,
      update,
      trackingData: expandedTrackingData,
      selectedTexture,
      selectedColor,
      selectedWindow,
    });
  };

  const undoDesignChange = () => send({ type: 'UNDO_DESIGN_CHANGE' });
  const redoDesignChange = () => {
    send({ type: 'REDO_DESIGN_CHANGE' });
  };
  const createInspireMeImage = () => {
    send({
      type: 'CREATE_INSPIRE_ME_IMAGE',
    });
  };

  const waitForInspireMeImage = () => {
    send({
      type: 'WAIT_FOR_INSPIRE_ME_IMAGE',
      data: {
        frozenRenderURL: '',
      },
    });
  };

  const clearInspireMeImage = () => {
    send({
      type: 'CLEAR_INSPIRE_ME_IMAGE',
      data: {
        image: '',
        designNameToRestore: '',
      },
    });
  };

  const setInspireMeImage = (image: string) => {
    send({
      type: 'SET_INSPIRE_ME_IMAGE',
      data: {
        image,
      },
    });
  };

  const updateDesignConfigFromBuilding = () => {
    send({
      type: 'UPDATE_DESIGN_FROM_BUILDING',
    });
  };

  return (
    <context.Provider
      // See DS-846
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        historyState: machineState.context,
        updateDesign,
        resetDesign,
        undoDesignChange,
        redoDesignChange,
        canUndo,
        canRedo,
        updateDesignConfigFromBuilding,
        createInspireMeImage,
        clearInspireMeImage,
        waitForInspireMeImage,
        setInspireMeImage,
      }}
    >
      {children}
    </context.Provider>
  );
};

const GuestHistoryProvider = ({ children }: { children: React.ReactNode }) => {
  const mode = useMode();
  const [historyState, setHistoryState] = useState(defaultContext.historyState);

  const resetDesign = () => setHistoryState(defaultContext.historyState);

  const trackProductSelected = ({
    category,
    trackingData,
    productSelectedAttributes,
  }: {
    category: FeatureCategories;
    trackingData: UpdateDesignPropsTrackingData;
    productSelectedAttributes: ExpandedProductSelectedAttributes;
  }) => {
    const trackData: ProductSelected = {
      ...trackingData,
      ...productSelectedAttributes,
      design_studio_mode: mode,
      page_or_screen_name: Route.DESIGN_STUDIO.page as string,
      product: trackingData.product || category,
    };
    try {
      productSelected(trackData);
    } catch (e) {
      sharedOptions.logError(historyState, { type: '', data: e as Error });
    }
  };

  const updateDesign = ({
    selectedWindow,
    selectedTexture,
    selectedColor,
    category,
    update,
    trackingData,
  }: UpdateDesignProps) => {
    const building = macheteBuildingRef.current;
    let designConfig;

    if (building) {
      if (update.targetAppearanceGroup) {
        /**
         * User is changing wall group
         */
        changeAppearanceOfWallMaterialGroup(
          building.designer,
          category,
          update,
        );
      } else {
        /**
         * User is changing all elements in a category
         */
        applyDesignUpdateThroughTemplateSystem(
          building.designer,
          category,
          update,
        );
      }
      designConfig = getCurrentDesignConfig(building.designer);
    }
    const trackingDesignConfig = newTrackingDesignConfig({
      trackingDesignConfig: historyState.trackingDesignConfig,
      selectedWindow,
      selectedTexture,
      selectedColor,
      category,
    });
    const productSelectedAttributes = getProductSelectedAttributes({
      trackingDesignConfig,
      currentCategory: category,
      designConfig: getGuardedLegacyConfig(historyState.designConfig),
    });
    let { designUpdates } = historyState;
    if (isProductSelected(productSelectedAttributes)) {
      designUpdates += 1;
      trackProductSelected({
        category,
        trackingData,
        productSelectedAttributes,
      });
    }
    setHistoryState({
      ...historyState,
      // @ts-ignore
      designConfig,
      trackingDesignConfig,
      designUpdates,
    });
  };

  const undoDesignChange = () => null;
  const redoDesignChange = () => null;

  return (
    <context.Provider
      // See DS-846
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        historyState,
        updateDesign,
        resetDesign,
        undoDesignChange,
        redoDesignChange,
        canUndo: false,
        canRedo: false,
        updateDesignConfigFromBuilding: () => {},
        createInspireMeImage: () => {},
        clearInspireMeImage: () => {},
        waitForInspireMeImage: () => {},
        setInspireMeImage: () => {},
      }}
    >
      {children}
    </context.Provider>
  );
};

const Provider = ({
  mode,
  jobId,
  children,
  viewUserId,
  shareToken,
}: {
  mode: Mode;
  jobId?: number;
  children: React.ReactNode;
  viewUserId?: number;
  shareToken?: string;
}) => {
  const [isNotConnectedToFirestore, setIsNotConnectedToFirestore] =
    useState(false);

  if (
    mode === Mode.GUEST ||
    mode === Mode.PREVIEW ||
    isNotConnectedToFirestore
  ) {
    return <GuestHistoryProvider>{children}</GuestHistoryProvider>;
  }

  if (jobId) {
    return (
      <ConnectedProvider
        jobId={jobId}
        setIsNotConnectedToFirestore={setIsNotConnectedToFirestore}
        shareToken={shareToken}
        viewUserId={viewUserId}
      >
        {children}
      </ConnectedProvider>
    );
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export { GuestHistoryProvider, ConnectedProvider, Provider, context };
